import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container", "panel", "messages", "input", "sendButton"];
  static outlets = ["reveal-bi-embed"];
  static values = {
    datasourceId: { type: String, default: "DataWarehouse" },
    open: { type: Boolean, default: false }
  };

  connect() {
    this.client = null;
    this.streamingMessageDiv = null;
    this.streamedText = "";
  }

  toggle() {
    this.openValue = !this.openValue;
    this.panelTarget.classList.toggle("reveal-ai-chat--open", this.openValue);
    this.containerTarget.classList.toggle("reveal-ai-chat-container--open", this.openValue);
    if (this.openValue) {
      this.inputTarget.focus();
    }
  }

  async send(event) {
    event?.preventDefault();
    const message = this.inputTarget.value.trim();
    if (!message) return;

    this.inputTarget.value = "";
    this.addMessage("user", message);
    this.setSending(true);

    try {
      await this.ensureClient();
      const embedController = this.revealBiEmbedOutlet;
      const dashboard = embedController.getDashboard();

      const sendOpts = {
        message,
        datasourceId: this.datasourceIdValue,
        dashboard,
        stream: true
      };
      const stream = await this.client.ai.chat.sendMessage(sendOpts);

      this.streamedText = "";
      this.streamingMessageDiv = null;

      stream.on("progress", (progressMessage) => {
        this.addMessage("log", progressMessage);
      });

      stream.on("text", (chunk) => {
        this.streamedText += chunk;
        if (!this.streamingMessageDiv) {
          this.streamingMessageDiv = this.createStreamingMessage();
        }
        this.streamingMessageDiv.innerHTML = window.marked.parse(this.streamedText);
        this.scrollToBottom();
      });

      stream.on("error", (error) => {
        this.addMessage("error", error);
      });

      const result = await stream.finalResponse();

      // Finalize the streaming message
      if (this.streamingMessageDiv) {
        this.streamingMessageDiv.closest(".reveal-ai-chat__message")
          ?.classList.replace("reveal-ai-chat__message--streaming", "reveal-ai-chat__message--assistant");
        this.streamingMessageDiv = null;
      }

      // If there's an explanation but no streaming text was shown, add it
      if (result.explanation && !this.streamedText) {
        this.addMessage("assistant", result.explanation);
      }

      // Load the AI-generated dashboard
      if (result.dashboard) {
        await embedController.setDashboard(result.dashboard);
        this.addMessage("log", "Dashboard updated.");
      }
    } catch (error) {
      console.error("Reveal AI chat request failed:", error); // eslint-disable-line no-console
      this.addMessage("error", this.describeSendFailure(error));
    } finally {
      this.setSending(false);
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      this.send(event);
    }
  }

  // -- Private helpers --

  // @revealbi/api throws an ApiError carrying `status` and the parsed `data` body.
  describeSendFailure(error) {
    const status = error?.status;
    if (status === 401) {
      return "Your session has expired. Reload the page to continue using AI chat.";
    }
    const detail = error?.data?.error || error?.message || String(error);
    return status
      ? `Failed to send message (HTTP ${status}): ${detail}`
      : `Failed to send message: ${detail}`;
  }

  async ensureClient() {
    if (!this.client) {
      this.client = window.rv.RevealSdkClient.getInstance();
    }
  }

  addMessage(role, content) {
    const wrapper = document.createElement("div");
    wrapper.className = `reveal-ai-chat__message reveal-ai-chat__message--${role}`;

    const avatar = document.createElement("span");
    avatar.className = "reveal-ai-chat__avatar";
    const icons = { user: "\u{1F464}", assistant: "\u{1F916}", log: "\u{1F4DD}", error: "\u26A0\uFE0F" };
    avatar.textContent = icons[role] || "\u2139\uFE0F";

    const body = document.createElement("div");
    body.className = "reveal-ai-chat__body";
    if (role === "assistant") {
      body.innerHTML = window.marked.parse(content);
    } else {
      body.textContent = content;
    }

    wrapper.appendChild(avatar);
    wrapper.appendChild(body);
    this.messagesTarget.appendChild(wrapper);
    this.scrollToBottom();
  }

  createStreamingMessage() {
    const wrapper = document.createElement("div");
    wrapper.className = "reveal-ai-chat__message reveal-ai-chat__message--streaming";

    const avatar = document.createElement("span");
    avatar.className = "reveal-ai-chat__avatar";
    avatar.textContent = "\u{1F916}";

    const body = document.createElement("div");
    body.className = "reveal-ai-chat__body";

    wrapper.appendChild(avatar);
    wrapper.appendChild(body);
    this.messagesTarget.appendChild(wrapper);
    this.scrollToBottom();
    return body;
  }

  setSending(isSending) {
    this.sendButtonTarget.disabled = isSending;
    this.inputTarget.disabled = isSending;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }
}
